import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChatData } from "../types";
import { format, parseISO, isToday, isYesterday } from "date-fns";
import { memo, useMemo, useState } from "react";
import { LuMessageSquare, LuX } from "react-icons/lu";
import HistoryListEmptyStateImage from "@/assets/list-empty-state.svg";
import NoDataFound from "@/components/common/emptyStates/NoDataFound";
import { SqButton } from "@/components/squircle-ui/button";
import { RiHistoryLine } from "@remixicon/react";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";

const groupByDate = (data: ChatData[]) => {
  return data.reduce((acc: { [key: string]: ChatData[] }, item) => {
    const date = format(parseISO(item.created_at), "yyyy-MM-dd");
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(item);
    return acc;
  }, {});
};

const DateLabel = memo(({ date }: { date: string }) => {
  const parsedDate = parseISO(date);

  let label = format(parsedDate, "MMMM d, yyyy");
  if (isToday(parsedDate)) {
    label = "Today";
  } else if (isYesterday(parsedDate)) {
    label = "Yesterday";
  }

  return <p className="!text-title-primary font-semibold">{label}</p>;
});

const InsightChatHistoryMenu = ({
  data,
  activeChatId,
  onChatSelect,
}: {
  data: ChatData[];
  activeChatId: string;
  onChatSelect: (chat: ChatData) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const groupedData = useMemo(() => groupByDate(data), [data]);
  const sanitizeValue = (str: string) => {
    // Replace any non-alphanumeric characters with hyphens and trim
    return str
      .replace(/[^a-zA-Z0-9]/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };
  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={(val) => setIsOpen(val)}>
        <PopoverTrigger asChild>
          <div className="cursor-pointer flex items-center justify-center">
            <SqTooltipProvider>
              <SqTooltip>
                <SqTooltipTrigger asChild>
                  <SqButton
                    variant={"icon"}
                    className="h-7 w-7 cursor-pointer"
                    bgColor="white"
                    hasOutline
                    shadow={"sm"}
                    hoverBgColor="#fbfbfb"
                  >
                    <RiHistoryLine size={14} color="#9AA3BD" className="shrink-0" />
                  </SqButton>
                </SqTooltipTrigger>
                <SqTooltipContent align="start">Chat History</SqTooltipContent>
              </SqTooltip>
            </SqTooltipProvider>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className={`px-[16px] py-[6px] w-[400px] h-[520px]`}
          side={"left"}
          align={"start"}
        >
          <div className="flex items-center justify-between">
            <div>All Chats</div>
            <Button
              className="p-0 hover:bg-transparent"
              variant="ghost"
              onClick={() => setIsOpen(!isOpen)}
            >
              <LuX size={18} />
            </Button>
          </div>

          {Object.keys(groupedData).length > 0 ? (
            <Command
              filter={(value, search) => {
                if (value.includes(search)) return 1;
                return 0;
              }}
              className="max-h-[460px]"
              style={{
                maxHeight: "460px",
              }}
            >
              <CommandInput
                placeholder="Search chat"
                wrapperClass="border rounded-sm mt-3"
                className="py-1 h-auto"
              />
              <CommandList className="max-h-[460px]">
                <CommandEmpty>
                  <NoDataFound
                    image={
                      <img
                        src={HistoryListEmptyStateImage}
                        alt="No Results Found"
                        className="mt-10"
                      />
                    }
                    title="No Results Found"
                    titleClass="text-sm !text-brand-primary-new font-medium"
                    description="We couldn't find any chat history matching your search. Start a new conversation or refine your search to see results here!"
                    descriptionClass="text-xs !text-title-secondary px-6"
                  />
                </CommandEmpty>
                {Object.keys(groupedData)?.map((date, index) => (
                  <CommandGroup
                    key={index}
                    heading={<DateLabel date={date} />}
                    className="text-center text-[11px] font-semibold"
                  >
                    {groupedData[date].map((item) => (
                      <CommandItem
                        key={item.id}
                        value={`${sanitizeValue(item.title)}-${item.id}`}
                        className={`cursor-pointer items-start text-xs text-left font-normal mt-1 gap-2 ${
                          activeChatId === item.id
                            ? "bg-accent text-accent-foreground font-medium"
                            : ""
                        }`}
                        onSelect={() => {
                          if (activeChatId !== item.id) onChatSelect(item);
                          setIsOpen(false);
                        }}
                      >
                        <LuMessageSquare
                          size={16}
                          className="shrink-0 stroke-1 mt-[0.3px]"
                        />
                        <span className="line-clamp-2">{item.title}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          ) : (
            <NoDataFound
              image={
                <img
                  src={HistoryListEmptyStateImage}
                  alt="History List Empty State Image"
                  className="mt-[80px]"
                />
              }
              title="No Chat History"
              titleClass="text-sm !text-brand-primary-new font-medium text-center"
              description="It looks like you haven't started a conversation yet.
                      Begin a new chat to see it here!"
              descriptionClass="text-xs !text-title-secondary px-6 text-center mb-2"
              hAuto
            />
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default InsightChatHistoryMenu;
